import { useRef } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import { ThemedNoDataIcon } from "assets/CXFIcons/Portal/Dashboard";

const ActionWrapper = ({ id, route, isExternal, handleEdit }) => {
  return isExternal ? (
    <SoftTypography
      component={isExternal ? "a" : Link}
      variant="button"
      color="text"
      href={route}
      to={route}
      target="_blank"
      sx={{
        lineHeight: 0,
        transition: "all 0.2s cubic-bezier(.34,1.61,.7,1.3)",
        p: 0.5,

        "&:hover, &:focus": {
          transform: "translateX(5px)"
        }
      }}
    >
      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
    </SoftTypography>
  ) : (
    <SoftTypography
      component="a"
      variant="button"
      color="text"
      onClick={() => handleEdit(id)}
      sx={{
        cursor: "pointer",
        lineHeight: 0,
        transition: "all 0.2s cubic-bezier(.34,1.61,.7,1.3)",
        p: 0.5,

        "&:hover, &:focus": {
          transform: "translateX(5px)"
        }
      }}
    >
      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
    </SoftTypography>
  );
};

function CategoriesList({
  title,
  categories,
  headerIcon,
  noDataMessage,
  isExternal,
  isLoading,
  isUser,
  bottomAction,
  listHeight,
  onClick = Function(),
  isFlows = false
}) {
  const tabMap = useRef(new Map());

  const handleEdit = (id) => {
    let tab = tabMap.current.get(id);

    if (!tab || tab.closed) {
      tab = window.open(`/designer/${id}`, "_blank");
      tabMap.current.set(id, tab);
    } else {
      tab.focus();
    }
  };

  const renderItems = categories.map(
    ({ id, color, icon, name, description, route, status, userAction }, key) => (
      <SoftBox
        key={`${description}-${key}`}
        component="li"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderRadius="lg"
        py={1}
        mb={categories.length - 1 === key ? 0 : 1}
      >
        <SoftBox display="flex" alignItems="center">
          {icon && (
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgColor={color}
              sx={{ background: color }}
              borderRadius="8px"
              shadow="none"
              color="white"
              width={isUser ? "auto" : "2rem"}
              height={isUser ? "auto" : "2rem"}
              variant="gradient"
            >
              <Icon
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: isUser ? "auto" : "1em",
                  height: isUser ? "auto" : "1em"
                }}
              >
                {icon}
              </Icon>
            </SoftBox>
          )}
          <SoftBox
            display="flex"
            position="relative"
            flexDirection="column"
            pl={icon ? 1 : 0}
            component={route ? "a" : ""}
            rel="noreferrer"
          >
            {!route ? (
              <button
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer"
                }}
                onClick={() => onClick(id)}
              >
                <SoftTypography opacity={0}>{name}</SoftTypography>
              </button>
            ) : (
              <></>
            )}

            <SoftBox display="flex" alignItems="center">
              <SoftTypography
                variant="button"
                color="text"
                fontWeight="medium"
                overflow="hidden"
                overflowy="auto"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                gutterBottom
                sx={{
                  width: {
                    sm: isFlows ? "200px" : isUser ? "120px" : "auto",
                    md: isUser ? "120px" : "auto",
                    lg: isFlows ? "200px" : isUser ? "120px" : "auto"
                  },
                  cursor: "pointer"
                }}
              >
                {name}
              </SoftTypography>
              {status && (
                <SoftBox display="flex" ml={2}>
                  <StatusBadge status={status} />
                </SoftBox>
              )}
            </SoftBox>
            <SoftTypography variant="caption" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftBox display="flex">
          {isUser ? (
            <>{userAction}</>
          ) : (
            <ActionWrapper
              id={id}
              route={route}
              isExternal={isExternal}
              handleEdit={handleEdit}
            />
          )}
        </SoftBox>
      </SoftBox>
    )
  );

  return (
    <Card sx={{ height: "100%", boxShadow: "none" }}>
      <SoftBox pt={3} px={3} sx={{ display: "flex", alignItems: "center" }}>
        {headerIcon && (
          <SoftBox width="auto" mr={1} mt={1}>
            {headerIcon}
          </SoftBox>
        )}
        <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {title}
        </SoftTypography>
      </SoftBox>
      <SoftBox px={3} pb={3} pt={2} height="100%">
        {isLoading ? (
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            p={0}
            m={0}
          >
            <CircularProgress />
          </SoftBox>
        ) : (
          <>
            <SoftBox
              component="ul"
              display="flex"
              height={listHeight || "187px"}
              flexDirection="column"
              sx={{ overflow: "hidden", overflowY: "auto", scrollbarWidth: "thin" }}
              p={0}
              m={0}
            >
              {renderItems.length ? (
                renderItems
              ) : (
                <SoftBox
                  component="li"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <SoftBox display="flex" flexDirection="column" alignItems="center">
                    <ThemedNoDataIcon />
                    {noDataMessage === "" ? (
                      <SoftTypography variant="h6" color="text">
                        No items
                      </SoftTypography>
                    ) : (
                      <SoftTypography variant="h6" color="text">
                        {noDataMessage}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
            {bottomAction}
          </>
        )}
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the CategoriesList
CategoriesList.propTypes = {
  title: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default CategoriesList;
